import React from 'react';
import { useRecoilValue } from 'recoil';
import { useTranslation } from 'react-i18next';
import { isMobile } from 'react-device-detect';
import { Box, DialogContent } from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { typeDate } from 'components/mui/Formatters';
import Currency from 'components/formaters/Currency';
import { ModalActionProps } from 'models/AutoModals';
import { TransactionModel } from 'models/TransactionModel';
import { CompanyType } from 'models/CompanyModel';
import auth from 'recoil/atoms/auth';


type TransactionsDialogProps = {
    transactions: TransactionModel[],
} & ModalActionProps

const TransactionsDialog = ({transactions}: TransactionsDialogProps) => {
    const my = useRecoilValue(auth);
    const {t} = useTranslation();

    const columns: GridColDef<TransactionModel>[] = [
        {
            field: 'id',
            headerName: '#',
            width: 70,
        },
        {
            field: 'created_at',
            headerName: t('date'),
            width: 100,
            ...typeDate,
        },
        ...my.type === CompanyType.Master ? [{
            field: 'author',
            headerName: t('name'),
            valueGetter: params => params.row.author.name,
            width: 190,
        }] as GridColDef<TransactionModel>[] : [],
        {
            field: 'description',
            headerName: t('description'),
            flex: 1,
            minWidth: 150,
        },
        {
            field: 'credit',
            headerName: t('credit'),
            width: 120,
            type: 'number',
            sortable: false,
            renderCell: params => !params.row.debit &&
                <Currency value={params.row.value.amount} currency={params.row.value.currency} />,
        },
        {
            field: 'debit',
            headerName: t('debit'),
            width: 120,
            type: 'number',
            sortable: false,
            renderCell: params => !!params.row.debit &&
                <><Currency value={params.row.value.amount} currency={params.row.value.currency} /></>,
        },
        {
            field: 'change',
            headerName: t('change'),
            width: 120,
            type: 'number',
            sortable: false,

            renderCell: params => <Box
                component="span"
                sx={{
                    color: params.row.debit ? undefined : 'warning.dark',
                }}
            >
                <Currency value={params.row.debit ? params.row.value.amount : -params.row.value.amount} currency={params.row.value.currency} />
            </Box>,
        },
        {
            field: 'balance',
            headerName: t('balance'),
            width: 120,
            type: 'number',
            sortable: false,
            renderCell: params => <Box
                component="span"
                sx={{
                    color: params.row.balance.amount >= 0 ? undefined :  'warning.dark',
                }}
            >
                <Currency value={params.row.balance.amount} currency={params.row.balance.currency} />
            </Box>,
        },
    ];

    return (
        <DialogContent sx={{height: '60vh'}}>
            <DataGrid
                rows={transactions}
                columns={columns}
                disableRowSelectionOnClick
                disableColumnFilter
                disableColumnMenu
                pageSizeOptions={[100]}
                initialState={{
                    columns: {
                        columnVisibilityModel: {
                            author: !isMobile,
                            debit: !isMobile,
                            credit: !isMobile,
                            change: isMobile,
                        },
                    },
                }}
            />
        </DialogContent>
    );
};

export default TransactionsDialog;
